import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import { AuthActions } from '../../core/auth/store/auth.actions';
import { resolveIdentity } from '../../core/auth/identity.util';
import { SessionService } from '../../core/auth/session.service';
import { FcraGateService } from '../tnc/fcra-gate.service';

@Component({
  selector: 'csd-callback',
  standalone: true,
  imports: [],
  templateUrl: './callback.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallbackComponent implements OnInit {
  private readonly oauthService = inject(OAuthService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fcraGate = inject(FcraGateService);
  private readonly session = inject(SessionService);

  ngOnInit(): void {
    this.subscribeToOAuthEvents();
    this.initializeLogin();
  }

  private subscribeToOAuthEvents(): void {
    this.oauthService.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: OAuthEvent) => {
        this.handleOAuthEvent(event);
      });
  }

  private handleOAuthEvent(event: OAuthEvent): void {
    // Token refresh on expiry is wired app-wide in app.config (this component is
    // destroyed right after login). Here we only complete the sign-in.
    if (event.type === 'token_received') {
      this.handleTokenReceived();
    }
  }

  private handleTokenReceived(): void {
    const identity = resolveIdentity(this.oauthService.getIdentityClaims());

    this.store.dispatch(
      AuthActions.loginSuccess({
        token: this.oauthService.getAccessToken(),
        expiresAt: this.oauthService.getAccessTokenExpiration(),
        ...identity,
      }),
    );

    // FCRA is a login-time step (FDD §3.1), but it's presented by the fcraGuard on
    // the shell — not here — so that a refresh taken while the modal is still up
    // re-shows it (the guard runs on every entry into the shell; this component
    // does not). Reset the per-login gate so the guard prompts for THIS login, then
    // hand off to the shell; the guard shows FCRA, then orgGuard resolves the org.
    this.fcraGate.reset();
    // Likewise forget any earlier session id so shellAccessGuard starts a new
    // audit session for THIS login.
    this.session.reset();
    void this.router.navigate(['/dashboard'], { replaceUrl: true });
  }

  private initializeLogin(): void {
    this.oauthService
      .loadDiscoveryDocument()
      .then(() => {
        void this.oauthService.tryLogin();
      })
      .catch(() => {
        this.store.dispatch(
          AuthActions.loginFailure({ error: 'Failed to load discovery document' }),
        );
        void this.router.navigate(['/signin']);
      });
  }
}
